import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";

function ConditionCareNotes({
  problems,
  patientId,
  apiBase,
  accentColor,
  onShowSnackbar,
  onAddCondition,
  onEditCondition,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [notesByProblem, setNotesByProblem] = useState({});
  const [loadingNotes, setLoadingNotes] = useState({});
  const [newNoteText, setNewNoteText] = useState("");
  const fetchedRef = useRef(new Set());

  const fetchNotes = useCallback(
    async (problemId) => {
      if (fetchedRef.current.has(problemId)) return;
      setLoadingNotes((prev) => ({ ...prev, [problemId]: true }));
      try {
        const res = await fetch(
          `${apiBase}/patients/${patientId}/problems/${problemId}/care-notes`
        );
        if (res.ok) {
          const data = await res.json();
          setNotesByProblem((prev) => ({ ...prev, [problemId]: data }));
          fetchedRef.current.add(problemId);
        }
      } catch {}
      setLoadingNotes((prev) => ({ ...prev, [problemId]: false }));
    },
    [apiBase, patientId]
  );

  const handleToggle = useCallback(
    (problemId) => {
      setExpandedId((prev) => {
        if (prev === problemId) return null;
        fetchNotes(problemId);
        return problemId;
      });
      setNewNoteText("");
    },
    [fetchNotes]
  );

  const handleAddNote = useCallback(
    async (problemId) => {
      if (!newNoteText.trim()) return;
      const optimistic = {
        id: Date.now(),
        note_text: newNoteText.trim(),
        created_by: "Current User",
        created_at: new Date().toISOString(),
      };
      setNotesByProblem((prev) => ({
        ...prev,
        [problemId]: [optimistic, ...(prev[problemId] || [])],
      }));
      setNewNoteText("");
      try {
        const res = await fetch(
          `${apiBase}/patients/${patientId}/problems/${problemId}/care-notes`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ note_text: optimistic.note_text }),
          }
        );
        if (res.ok) {
          const saved = await res.json();
          setNotesByProblem((prev) => ({
            ...prev,
            [problemId]: [
              saved,
              ...(prev[problemId] || []).filter((n) => n.id !== optimistic.id),
            ],
          }));
        } else {
          setNotesByProblem((prev) => ({
            ...prev,
            [problemId]: (prev[problemId] || []).filter((n) => n.id !== optimistic.id),
          }));
          onShowSnackbar({ open: true, message: "Failed to save care note", severity: "error" });
        }
      } catch {
        setNotesByProblem((prev) => ({
          ...prev,
          [problemId]: (prev[problemId] || []).filter((n) => n.id !== optimistic.id),
        }));
        onShowSnackbar({ open: true, message: "Network error saving care note", severity: "error" });
      }
    },
    [apiBase, patientId, newNoteText, onShowSnackbar]
  );

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const activeProblems = (problems || []).filter(
    (p) => p.status === "active" || p.status === "chronic"
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Typography
          sx={{ color: accentColor.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}
        >
          CONDITIONS
        </Typography>
        {onAddCondition && (
          <Tooltip title="Add condition" arrow>
            <Chip
              icon={<AddIcon sx={{ fontSize: 12 }} />}
              label="Add"
              size="small"
              onClick={onAddCondition}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                color: accentColor.main,
                borderColor: accentColor.main,
                "& .MuiChip-icon": { color: accentColor.main },
              }}
              variant="outlined"
            />
          </Tooltip>
        )}
      </Box>

      {activeProblems.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No active conditions
        </Typography>
      )}

      {activeProblems.map((problem) => {
        const isExpanded = expandedId === problem.id;
        const notes = notesByProblem[problem.id] || [];
        const loading = loadingNotes[problem.id];

        return (
          <Box
            key={problem.id}
            sx={{
              bgcolor: `${accentColor.main}08`,
              borderRadius: 1.5,
              overflow: "hidden",
              mb: 0.75,
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8rem", color: "text.primary" }}>
                {problem.name}
              </Typography>
              <Chip
                label={problem.status}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  bgcolor: `${accentColor.main}20`,
                  color: accentColor.main,
                }}
              />
              {onEditCondition && (
                <IconButton size="small" aria-label="Edit condition" onClick={() => onEditCondition(problem)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                </IconButton>
              )}
              <IconButton size="small" aria-label={isExpanded ? "Collapse care notes" : "Expand care notes"} onClick={() => handleToggle(problem.id)} sx={{ p: 0.25 }}>
                {isExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={isExpanded}>
              <Box
                sx={{
                  px: 1.25,
                  pb: 1,
                  borderTop: `1px dashed ${accentColor.main}30`,
                  bgcolor: "rgba(0,0,0,0.12)",
                }}
              >
                <Typography
                  sx={{ fontSize: "0.6rem", color: "text.secondary", letterSpacing: 0.3, mt: 0.75, mb: 0.5 }}
                >
                  CARE NOTES
                </Typography>

                {loading && (
                  <Typography variant="caption" color="text.secondary">
                    Loading...
                  </Typography>
                )}

                {!loading && notes.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    No notes yet
                  </Typography>
                )}

                {notes.map((note) => (
                  <Typography key={note.id} sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.4 }}>
                    <Box component="span" sx={{ color: "text.disabled", fontSize: "0.65rem" }}>
                      {fmtDate(note.created_at)} —
                    </Box>{" "}
                    {note.note_text}
                  </Typography>
                ))}

                <Box display="flex" gap={0.75} alignItems="center" mt={0.75}>
                  <TextField
                    size="small"
                    aria-label="Care note"
                    placeholder="Add care note..."
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote(problem.id);
                      }
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-input": { fontSize: "0.75rem", py: 0.5, px: 1 },
                    }}
                  />
                  <Chip
                    label="Add"
                    size="small"
                    onClick={() => handleAddNote(problem.id)}
                    sx={{
                      height: 24,
                      fontSize: "0.65rem",
                      color: accentColor.main,
                      borderColor: accentColor.main,
                    }}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

export default ConditionCareNotes;
